import { Typography } from '@packages/ui/shared';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { Ssm5VehicleMatch } from '../context/ssm5-connection.context';

import { useLastDefined } from '../hooks/use-last-defined';
import { DECISION_MODAL_WIDTH } from '../ssm5.constants';
import { ssm5TextStyles } from '../ssm5.styles';
import { Ssm5Modal } from './ssm5-modal';
import { Ssm5VehicleCard } from './ssm5-vehicle-card';

/** Props for {@link Ssm5VehicleMatchModal}. */
export type Ssm5VehicleMatchModalProps = {
    /** The matched vehicle awaiting the decision; the modal is shown while it is set. */
    match?: Ssm5VehicleMatch;
    /** Called when the technician declines to open the VIN session. */
    onCancel: () => void;
    /** Called with the match when the technician opens its VIN session. */
    onContinue: (match: Ssm5VehicleMatch) => void;
    /** The VIN of the VIN session the technician is in, if any; a match on another vehicle reads as a mismatch. */
    sessionVin?: string;
};

/** The copy the modal reads with, by what the technician is being asked. */
const COPY = {
    mismatch: { description: 'ssm5:vehicleMismatch.description', title: 'ssm5:vehicleMismatch.title' },
    newSession: { description: 'ssm5:vehicleMatch.description', title: 'ssm5:vehicleMatch.title' },
} as const;

/**
 * Modal asking the technician to continue into the VIN session of the vehicle the SSM5 session read,
 * or to switch to it when they are in another vehicle's session.
 *
 * @param props - {@link Ssm5VehicleMatchModalProps}
 * @returns the modal
 */
export function Ssm5VehicleMatchModal({ match, onCancel, onContinue, sessionVin }: Ssm5VehicleMatchModalProps) {
    const { t } = useTranslation();
    // Once answered the modal fades out, and until it is gone it keeps showing the question it asked.
    const shownMatch = useLastDefined(match);
    const copy = useLastDefined(match && matchCopy(match, sessionVin)) ?? COPY.newSession;

    const continueToVinSession = useCallback(() => {
        if (match) {
            onContinue(match);
        }
    }, [match, onContinue]);

    const actions = {
        primary: { onPress: continueToVinSession, title: t('ssm5:vehicleMatch.continue') },
        secondary: { onPress: onCancel, title: t('ssm5:vehicleMatch.cancel') },
    };

    return (
        <Ssm5Modal
            actions={actions}
            onHide={onCancel}
            show={match !== undefined}
            title={t(copy.title)}
            width={DECISION_MODAL_WIDTH}
        >
            <Typography style={ssm5TextStyles.body} variant="default">
                {t(copy.description)}
            </Typography>
            {shownMatch && <Ssm5VehicleCard vehicle={shownMatch.vehicle} />}
        </Ssm5Modal>
    );
}

/**
 * Which copy a match calls for: the mismatch copy when the technician is in another vehicle's session.
 *
 * @param match - the matched vehicle
 * @param sessionVin - the VIN of the session they are in, if any
 * @returns the title and description keys
 */
function matchCopy(match: Ssm5VehicleMatch, sessionVin: string | undefined) {
    return sessionVin !== undefined && sessionVin !== match.session.vin ? COPY.mismatch : COPY.newSession;
}
